import Joi from "joi-browser";

function requiredErrorsAsString(errors) {
    const err = errors[0];

    if (err.type === "object.child") {
        return errorsAsStringForChild(err.context?.reason);
    }

    return {
        template: " {{message}}",
        context: {
            errors: errors.length,
            message: "is required",
        },
    };
}

function positiveNumberError(errors) {
    return {
        template: " {{message}}",
        context: {
            errors: errors.length,
            message: "can't be less than or equal 0",
        },
    };
}

function errorsAsStringForChild(errors) {
    const err = errors[0];
    let strError = "";
    switch (err.type) {
        case "number.min":
            strError = "can't be less than or equal 1";
            break;
        case "number.max":
            strError = "must be less than or equal to 100";
            break;
        default:
            strError = "is required";
    }
    return {
        template: " {{message}}",
        context: {
            errors: errors.length,
            message: strError,
        },
    };
}

function errorsAsString(errors) {
    const err = errors[0];
    let strError = "";
    switch (err.type) {
        case "number.min":
            strError = "can't be less than one.";
            break;
        case "string.regex.base":
            strError = "should be a number.";
            break;
        case "array.base":
            strError = "Must be an array";
            break;
        case "number.max":
            strError = "Value must be less than or equal to 10";
            break;
    }
    return {
        template: " {{message}}",
        context: {
            errors: errors.length,
            message: strError,
        },
    };
}

const UploadModelSchema = Joi.object().keys({
    description: Joi.string()
        .required()
        .label("Model Description")
        .error((errors) => {
            return requiredErrorsAsString(errors);
        }),
    version: Joi.string()
        .required()
        .label("Version Name")
        .error((errors) => {
            return requiredErrorsAsString(errors);
        }),
    numberOfMeasurements: Joi.number().required().positive(),
    threshold: Joi.number().min(1).max(10).allow(""),
    isVisible: Joi.boolean(),
    executionOrder: Joi.when(Joi.ref("$isCalculatedField"), {
        is: false,
        then: Joi.number().required(),
    }),
    RMSECalib: Joi.when(Joi.ref("$isCalculatedField"), {
        is: false,
        then: Joi.number().required(),
    }),
    R2Calib: Joi.when(Joi.ref("$isCalculatedField"), {
        is: false,
        then: Joi.number().required(),
    }),
    RMSECV: Joi.when(Joi.ref("$isCalculatedField"), {
        is: false,
        then: Joi.number().required(),
    }),
    R2CV: Joi.when(Joi.ref("$isCalculatedField"), {
        is: false,
        then: Joi.number().required(),
    }),
    RMSETest: Joi.when(Joi.ref("$isCalculatedField"), {
        is: false,
        then: Joi.number().required(),
    }),
    R2Test: Joi.when(Joi.ref("$isCalculatedField"), {
        is: false,
        then: Joi.number().required(),
    }),
    avgReadings: Joi.when(Joi.ref("$isCalculatedField"), {
        is: false,
        then: Joi.number().positive().allow(0).required(),
    }),
    avgPredictions: Joi.when(Joi.ref("$isCalculatedField"), {
        is: false,
        then: Joi.when("avgReadings", {
            is: Joi.number().less(1),
            then: Joi.number().valid(1).required(),
            otherwise: Joi.number().allow(0).positive().required(),
        }),
    }),
    BiasCV: Joi.when(Joi.ref("$isCalculatedField"), {
        is: false,
        then: Joi.number().allow("").allow(null),
    }),
    BiasTest: Joi.when(Joi.ref("$isCalculatedField"), {
        is: false,
        then: Joi.number().allow("").allow(null),
    }),
    RPDCV: Joi.when(Joi.ref("$isCalculatedField"), {
        is: false,
        then: Joi.number().allow("").allow(null),
    }),
    RPDTest: Joi.when(Joi.ref("$isCalculatedField"), {
        is: false,
        then: Joi.number().allow("").allow(null),
    }),
    numberOfSamples: Joi.number().allow(""),
    operationsJson: Joi.when(Joi.ref("$isStandard"), {
        is: true,
        then: Joi.array()
            .label("Operations JSON")
            .items(
                Joi.object().keys({
                    id: Joi.number().required(),
                    params: Joi.array().required(),
                    index: Joi.number().required(),
                })
            )
            .min(1)
            .required(),
        otherwise: Joi.when(Joi.ref("$operationsJson"), {
            is: true,
            then: Joi.array().items(
                Joi.object().keys({
                    id: Joi.number().required(),
                    params: Joi.array().required(),
                    index: Joi.number().required(),
                })
            ),
        }),
    }),
    operationsFile: Joi.when(Joi.ref("$isEigenvector"), {
        is: true,
        then: Joi.when(Joi.ref("$isOperationsFileAlreadyExists"), {
            is: true,
            then: Joi.optional(),
            otherwise: Joi.object()
                .keys({
                    name: Joi.string().required(),
                    data: Joi.string().required(),
                })
                .required(),
        }),
        otherwise: Joi.optional(),
    }).error((errors) => {
        return requiredErrorsAsString(errors);
    }),
    QResidual: Joi.when(Joi.ref("$isEigenvector"), {
        is: true,
        then: Joi.object().keys({
            booleanValue: Joi.bool(),
            inputValue: Joi.when(Joi.ref("booleanValue"), {
                is: true,
                then: Joi.number().positive().max(100).required(),
                otherwise: Joi.number().allow("").optional(),
            }),
        }),
    }).error((errors) => positiveNumberError(errors)),
    ReducedHotellingT2: Joi.when(Joi.ref("$isEigenvector"), {
        is: true,
        then: Joi.object().keys({
            booleanValue: Joi.bool(),
            inputValue: Joi.when(Joi.ref("booleanValue"), {
                is: true,
                then: Joi.number().positive().max(100).required(),
                otherwise: Joi.number().allow("").optional(),
            }),
        }),
    }).error((errors) => positiveNumberError(errors)),
    outlierDetectionOperation: Joi.when(Joi.ref("$isMultipleOutlierTypes"), {
        is: true,
        then: Joi.string().valid("and", "or").required(),
        otherwise: Joi.string().optional(),
    }).error((errors) => {
        return requiredErrorsAsString(errors);
    }),
    operationsScreening: Joi.array()
        .items(
            Joi.object().keys({
                id: Joi.number(),
                params: Joi.array(),
                index: Joi.number(),
            })
        )
        .allow(null)
        .error((errors) => {
            return errorsAsString(errors);
        }),
    supportedAccessoryIds: Joi.array()
        .label("Supported Accessories")
        .allow(null)
        .error((errors) => {
            return errorsAsString(errors);
        }),
    resultsFile: Joi.when(Joi.ref("$isFileAlreadyExists"), {
        is: true,
        then: Joi.optional(),
        otherwise: Joi.object().keys({
            name: Joi.string().allow(null),
            data: Joi.string().allow(null),
        }),
    })
        .label("Results file")
        .error((errors) => {
            return requiredErrorsAsString(errors);
        }),
    minValue: Joi.number().positive().allow(0).required(),
    maxValue: Joi.number().positive().allow(0).required(),
});

const CreateModelSchema = Joi.object().keys({
    name: Joi.string()
        .trim()
        .required()
        .label("Model Name")
        .error((errors) => {
            return requiredErrorsAsString(errors);
        }),
    options: Joi.object().keys({
        testingMethod: Joi.object().required().label("Testing Method"),
        modelType: Joi.object().required().label("Model Type"),
        crossValidation: Joi.object()
            .label("Cross Validation")
            .required()
            .keys({
                value: Joi.number()
                    .integer()
                    .label("K-Fold")
                    .when(Joi.ref("$limited"), {
                        is: true,
                        then: Joi.number().min(1).max(Joi.ref("$maxFold")),
                    })
                    .error((errors) => {
                        return errorsAsString(errors);
                    }),
            }),
    }),
});

const CloneModelSchema = Joi.object().keys({
    modelId: Joi.string()
        .trim()
        .required()
        .error((errors) => {
            return requiredErrorsAsString(errors);
        }),
    organization: Joi.object()
        .label("Organization")
        .required()
        .keys({
            value: Joi.string()
                .trim()
                .required()
                .label("Organization")
                .error((errors) => {
                    return requiredErrorsAsString(errors);
                }),
        })
        .error((errors) => {
            return requiredErrorsAsString(errors);
        }),
    modelParameters: Joi.object().min(1).required().label("Model Parameters"),
    user: Joi.object()
        .label("User")
        .required()
        .keys({
            value: Joi.string()
                .trim()
                .required()
                .label("User")
                .error((errors) => {
                    return requiredErrorsAsString(errors);
                }),
        })
        .error((errors) => {
            return requiredErrorsAsString(errors);
        }),
});

const ParametersSettingsCollection = Joi.object().pattern(
    /^/,
    Joi.object().keys({
        unit: Joi.string().max(10).trim().required(),
        decimalPlaces: Joi.number()
            .min(0)
            .max(4)
            .required()
            .label("Decimal Places"),
    })
);

const UnitSchema = Joi.object().keys({
    unit: Joi.object()
        .keys({
            label: Joi.string().allow(""),
            value: [Joi.string().allow(""), Joi.number()],
        })
        .required(),
    decimalPlaces: Joi.number()
        .min(0)
        .max(4)
        .required()
        .label("Decimal Places"),
});

const ParametersSettings = Joi.array().items(
    Joi.object().keys({
        name: Joi.string()
            .regex(/^[^0-9][a-zA-Z0-9_ -]*$/)
            .trim()
            .required()
            .label("Technical Name")
            .error((errors) => {
                const err = errors[0];
                let strError = "";
                switch (err.type) {
                    case "any.empty":
                        strError = "is required";
                        break;
                    case "string.regex.base":
                        strError = "is invalid";
                        break;
                }
                return {
                    template: " {{message}}",
                    context: {
                        errors: errors.length,
                        message: strError,
                    },
                };
            }),
        // localizations: Joi.object().keys({
        //     en: Joi.string().trim().required().label("Display Name"),
        // }),
        parameterDetails: Joi.array().items(UnitSchema),
    })
);

const ClassificationParameters = Joi.array().items(
    Joi.object().keys({
        classifier: Joi.object(),
    })
);

const ModelOptionsSchema = Joi.object({
    isDynamicSorting: Joi.bool(),
    dynamicSortedParameterNames: Joi.when("isDynamicSorting", {
        is: true,
        then: Joi.array().min(1),
    }),
});

export default {
    UploadModelSchema,
    CreateModelSchema,
    CloneModelSchema,
    ParametersSettingsCollection,
    ParametersSettings,
    ClassificationParameters,
    ModelOptionsSchema,
};
